import { apiGet } from './client';

/**
 * The site's setup to-do list, built by the gateway (services/api-gateway lib/setup-checklist.ts)
 * for the ACTIVE site. One list for the Dashboard's "Finish setting up" card, the setup wizard,
 * the Studio chat and the BLT panel, and the same one BLT reads with its setup tool.
 */

/** The facts the list is built from. */
export interface SetupChecklistState {
  plan: string | null;
  /** The site's brand has colours or a logo set. */
  hasBrand: boolean;
  /** The site has been built (a look picked or an AI build), i.e. it is not empty. */
  hasDesign: boolean;
  /** The agent's profile has a bio (the first thing the wizard's AI writer fills). */
  hasBio: boolean;
  /** An MLS feed is live for this site. */
  mlsConnected: boolean;
  hasHeadshot: boolean;
  /** Every required legal document is published. null = unknown (the read failed). */
  legalPublished: boolean | null;
  /** The address new-lead alerts go to, or null when none is set. */
  leadEmail: string | null;
  /** A custom domain is connected and verified. */
  hasCustomDomain: boolean;
  /** Items whose read failed on the gateway: not done, and not known to be undone either. */
  unchecked?: SetupTodo['id'][];
}

export interface SetupTodo {
  id: 'brand' | 'design' | 'plan' | 'mls' | 'headshot' | 'legal' | 'leads' | 'domain' | 'publish';
  label: string;
  /** Why the agent has to do it themselves, in one line. */
  detail: string;
  /** Builder route that does it. */
  href: string;
  done: boolean;
  optional?: boolean;
  /** Its read failed on the gateway: shown as "couldn't check", never as done or to do. */
  unchecked?: boolean;
}

export interface SetupChecklist {
  state: SetupChecklistState;
  items: SetupTodo[];
}

const isItem = (i: unknown): i is SetupTodo => {
  const x = i as Partial<SetupTodo> | null;
  return !!x && typeof x === 'object' && typeof x.id === 'string' && typeof x.label === 'string'
    && typeof x.detail === 'string' && typeof x.href === 'string' && typeof x.done === 'boolean';
};

/** What was wrong with the answer, for the console and the error, without the answer itself. */
function malformed(data: Partial<SetupChecklist> | null): string | null {
  if (!data) return 'the answer was empty';
  if (!Array.isArray(data.items)) return 'it had no items list';
  if (!data.state || typeof data.state !== 'object') return 'it had no state';
  const bad = data.items.findIndex((i) => !isItem(i));
  if (bad >= 0) return `item ${bad} is missing its id, label, detail, link or done`;
  return null;
}

/** An answer without the list, or with an item missing a field (an older gateway, a proxy's empty
 *  body), is a failed read, so every caller treats it like one (no card, no to-dos) instead of
 *  rendering half a list. */
export async function getSetupChecklist(): Promise<SetupChecklist> {
  const data = await apiGet<Partial<SetupChecklist> | null>('/setup/checklist');
  const problem = malformed(data);
  if (problem || !data?.items || !data.state) {
    console.warn(`[setup] GET /setup/checklist: ${problem}`);
    throw new Error(`The setup checklist could not be read: ${problem}.`);
  }
  return { state: data.state, items: data.items };
}

/**
 * The one query for the active site's checklist: the Dashboard card, the BLT panel and the Studio
 * chat's flows all read it through this key, so an open Studio and Dashboard share one read. The
 * plan is in the key so an upgrade refreshes it (the gateway reads the plan itself).
 */
export function setupChecklistQuery(activeSiteId: string | null | undefined, plan: string | null | undefined) {
  return { queryKey: ['setup-checklist', activeSiteId ?? null, plan ?? null] as const, queryFn: getSetupChecklist, staleTime: 60_000 };
}
